import type { SelectableChartType } from '../dashboard-capabilities/types/chart';
import type { MetricValues } from '../dashboard-capabilities/types/metric';
import { DataType } from './data-type';

const D = DataType;

export type MetricDataTypeCapability = {
  dataTypes: DataType[];
  defaultDataType: DataType;
  excludedSelectableChartTypes?: readonly SelectableChartType[];
  /**
   * Distribution-capable sibling to use when this metric is charted as a
   * distribution but its own `dataTypes` carry no box/distribution shape
   * (e.g. the aggregate `salary` defers to the granular `base_salary`).
   */
  distributionVariant?: MetricValues;
};

/** Group-structure counts: one number per company, nothing to break down. */
const SUBSIDIARY_SHAPES: DataType[] = [D.SINGLE];

const EXCLUDE_STACKED_BAR_CHART: Pick<
  MetricDataTypeCapability,
  'excludedSelectableChartTypes'
> = {
  excludedSelectableChartTypes: ['stacked-bar'],
};

/** Composition-style metrics: bar, line, table, text (no segmented bars). */
const COMPOSITION_SIMPLE: DataType[] = [
  D.SINGLE,
  D.TIMESERIES,
  D.BREAKDOWN_TIMESERIES,
  D.TABLE_ROWS,
];

/** Composition with breakdown / treemap / map-capable headcount. */
const COMPOSITION_FULL: DataType[] = [
  ...COMPOSITION_SIMPLE,
  D.BREAKDOWN_SINGLE,
  D.MAP,
];

/** Total spend & similar: breakdown + treemap, no distribution/box/geo. */
const TOTAL_SPEND_SHAPES: DataType[] = [
  D.SINGLE,
  D.BREAKDOWN_SINGLE,
  D.TIMESERIES,
  D.BREAKDOWN_TIMESERIES,
  D.TABLE_ROWS,
];

/** Pay metrics supporting box & distribution. */
const PAY_POLYMORPHIC: DataType[] = [
  D.SINGLE,
  D.BREAKDOWN_SINGLE,
  D.TIMESERIES,
  D.BOX,
  D.DISTRIBUTION,
  D.TABLE_ROWS,
];

const TRANSITION_SHAPES: DataType[] = [
  D.BREAKDOWN_SINGLE,
  D.FLOW_BREAKDOWN_SINGLE,
  D.SINGLE,
  D.TABLE_ROWS,
];

const TRANSITION_INFLOW_OUTFLOW_SHAPES: DataType[] = [
  D.BREAKDOWN_SINGLE,
  D.FLOW_BREAKDOWN_SINGLE,
];

const EXCLUDE_NON_DIVERGING_BAR_COMBO_CHARTS: Pick<
  MetricDataTypeCapability,
  'excludedSelectableChartTypes'
> = {
  excludedSelectableChartTypes: [
    'bar',
    'stacked-bar',
    'grouped-bar',
    'diverging-bar',
    'treemap',
    'table',
  ],
};

const COMPOSITION_WITH_DIVERGING_BAR: DataType[] = [
  ...COMPOSITION_SIMPLE,
  D.BREAKDOWN_SINGLE,
];

const SENTIMENT_TOP_BOTTOM_SHAPES: DataType[] = [
  D.SINGLE,
  D.BREAKDOWN_SINGLE,
  D.TABLE_ROWS,
];

const POSTING_STANDARD_SHAPES: DataType[] = [
  D.SINGLE,
  D.BREAKDOWN_SINGLE,
  D.TIMESERIES,
  D.TABLE_ROWS,
];

const POSTING_VOLUME_SHAPES: DataType[] = [
  D.SINGLE,
  D.BREAKDOWN_SINGLE,
  D.TIMESERIES,
  D.BREAKDOWN_TIMESERIES,
  D.TABLE_ROWS,
];

const SCREENER_TABLE_SHAPES: DataType[] = [D.TABLE_ROWS];

const SCREENER_HEADCOUNT_SHAPES: DataType[] = [
  D.TABLE_ROWS,
  D.SINGLE,
  D.BREAKDOWN_SINGLE,
];

const MARKET_CONDITION_SHAPES: DataType[] = [
  ...COMPOSITION_SIMPLE,
  D.BREAKDOWN_SINGLE,
];

/**
 * For each metric, data shapes the data layer can produce. Derived from the legacy
 * chart allowlist and chart input requirements so chart↔metric compatibility
 * stays equivalent while routing uses DataType.
 */
export const METRIC_DATA_TYPE_REGISTRY: Record<
  MetricValues,
  MetricDataTypeCapability
> = {
  headcount: { dataTypes: COMPOSITION_FULL, defaultDataType: D.SINGLE },
  growth_rate: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  hiring_rate: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  attrition_rate: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  tenure: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  salary: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
    distributionVariant: 'base_salary',
  },
  share_of_headcount: { dataTypes: [], defaultDataType: D.SINGLE },

  transitions_inflow: {
    dataTypes: TRANSITION_SHAPES,
    defaultDataType: D.BREAKDOWN_SINGLE,
  },
  transitions_outflow: {
    dataTypes: TRANSITION_SHAPES,
    defaultDataType: D.BREAKDOWN_SINGLE,
  },
  transitions_inflows_and_outflows: {
    dataTypes: TRANSITION_INFLOW_OUTFLOW_SHAPES,
    defaultDataType: D.BREAKDOWN_SINGLE,
    ...EXCLUDE_NON_DIVERGING_BAR_COMBO_CHARTS,
  },

  posting_salary: {
    dataTypes: POSTING_STANDARD_SHAPES,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  time_to_fill: {
    dataTypes: POSTING_STANDARD_SHAPES,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  postings_active: {
    dataTypes: POSTING_STANDARD_SHAPES,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  postings_new: {
    dataTypes: POSTING_STANDARD_SHAPES,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  postings_removed: {
    dataTypes: POSTING_STANDARD_SHAPES,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  postings_volume: {
    dataTypes: POSTING_VOLUME_SHAPES,
    defaultDataType: D.TIMESERIES,
    ...EXCLUDE_STACKED_BAR_CHART,
  },

  total_spend_salary: {
    dataTypes: TOTAL_SPEND_SHAPES,
    defaultDataType: D.SINGLE,
  },
  total_spend_compensation: {
    dataTypes: TOTAL_SPEND_SHAPES,
    defaultDataType: D.SINGLE,
  },
  total_compensation: {
    dataTypes: PAY_POLYMORPHIC,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  base_salary: {
    dataTypes: PAY_POLYMORPHIC,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  contractor_pay_rate: {
    dataTypes: PAY_POLYMORPHIC,
    defaultDataType: D.SINGLE,
  },
  base_salary_percentiles: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  total_compensation_percentiles: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  contractor_pay_rate_percentiles: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },

  overall: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  sentiment_top_bottom: {
    dataTypes: SENTIMENT_TOP_BOTTOM_SHAPES,
    defaultDataType: D.BREAKDOWN_SINGLE,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  business_outlook: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  career_growth: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  compensation: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  benefits: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  healthcare: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  retirement_pension: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  childcare: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  education_assistance: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  wellness_programs: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  work_life_balance: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  diversity_and_inclusion: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  leadership: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  culture: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  remote_work: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  onboarding: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  mentorship: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  technology: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  promotion_opportunities: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  paid_time_off: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  transparency: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  efficiency_and_bureaucracy: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  product_and_service_quality: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  prestige: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  coworkers: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  mission_and_vision: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  workplace_facilities: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  training: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  clients_and_customers: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  company_growth: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  schedule_flexibility: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  job_security: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  overtime: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  family_support: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  commission: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  independence: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  commute: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  safety: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  internal_communication: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  office_politics: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  harassment: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  honesty: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  ethics: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  commitment: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  compliance: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  integrity: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  positive_review: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },
  negative_review: {
    dataTypes: COMPOSITION_WITH_DIVERGING_BAR,
    defaultDataType: D.SINGLE,
  },

  screener_headcount: {
    dataTypes: SCREENER_HEADCOUNT_SHAPES,
    defaultDataType: D.TABLE_ROWS,
    ...EXCLUDE_STACKED_BAR_CHART,
  },
  screener_growth: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  screener_growth_yoy: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  screener_hiring: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  screener_attrition: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  screener_salary: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  screener_tenure: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  screener_inflow: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  screener_outflow: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },

  td_users: { dataTypes: SCREENER_TABLE_SHAPES, defaultDataType: D.TABLE_ROWS },

  entity_summary: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  demand_supply: {
    dataTypes: MARKET_CONDITION_SHAPES,
    defaultDataType: D.SINGLE,
  },
  demand_supply_percentile: {
    dataTypes: MARKET_CONDITION_SHAPES,
    defaultDataType: D.SINGLE,
  },
  wage_pressure: {
    dataTypes: MARKET_CONDITION_SHAPES,
    defaultDataType: D.SINGLE,
  },
  wage_pressure_percentile: {
    dataTypes: MARKET_CONDITION_SHAPES,
    defaultDataType: D.SINGLE,
  },
  market_tightness: {
    dataTypes: MARKET_CONDITION_SHAPES,
    defaultDataType: D.SINGLE,
  },
  market_share: {
    dataTypes: SCREENER_TABLE_SHAPES,
    defaultDataType: D.TABLE_ROWS,
  },
  target_companies_rec: {
    dataTypes: MARKET_CONDITION_SHAPES,
    defaultDataType: D.SINGLE,
  },
  time_to_fill_percentile: {
    dataTypes: MARKET_CONDITION_SHAPES,
    defaultDataType: D.SINGLE,
  },

  subsidiary_count: {
    dataTypes: SUBSIDIARY_SHAPES,
    defaultDataType: D.SINGLE,
  },

  overview_map: { dataTypes: [D.MAP], defaultDataType: D.MAP },
};

const SAVED_QUERY_VARIABLE_SHAPES: DataType[] = [
  D.SINGLE,
  D.BREAKDOWN_SINGLE,
  D.TIMESERIES,
  D.BREAKDOWN_TIMESERIES,
  D.MAP,
  D.TABLE_ROWS,
];

const SAVED_QUERY_VARIABLE_CAPABILITY: MetricDataTypeCapability = {
  dataTypes: SAVED_QUERY_VARIABLE_SHAPES,
  defaultDataType: D.SINGLE,
};

export function isSavedQueryVariableMetric(
  metric: string
): metric is `sq:${string}` {
  return metric.startsWith('sq:');
}

export function getMetricDataTypeCapability(
  metric: MetricValues
): MetricDataTypeCapability {
  if (isSavedQueryVariableMetric(metric)) {
    return SAVED_QUERY_VARIABLE_CAPABILITY;
  }
  const entry =
    METRIC_DATA_TYPE_REGISTRY[metric as keyof typeof METRIC_DATA_TYPE_REGISTRY];
  if (entry === undefined) {
    throw new Error(`Unknown metric: ${String(metric)}`);
  }
  return entry;
}
